import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { markActivityNow } from '../Auth/session';
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Progress,
  Icon,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { CloseIcon, DownloadIcon, InfoIcon, RepeatIcon } from '@chakra-ui/icons';
import { useToast } from '@chakra-ui/react';
import Chatbot from '../../components/ChatBot/Chatbot';
import { StorageProviderSelector, StorageProvider } from '../../components/upload/StorageProviderSelector';
import { CloudStorageURLInput } from '../../components/upload/CloudStorageURLInput';
import { StorageProviderInstructions } from '../../components/upload/StorageProviderInstructions';
import { FiHardDrive, FiFolder } from 'react-icons/fi';
import { BACKEND_API } from '../../constants/backendApiEndpoints';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import FeedbackModal from '../../components/common/FeedbackModal';
import { useFeedback } from '../../hooks/useFeedback';

// Configure PDF worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ID validation
const ID_RE = /^[A-Z0-9_-]{3,32}$/;
const normalizeId = (v: string) => v.replace(/\s+/g, '').toUpperCase();

// Detect file type from filename
const getFileTypeFromName = (filename: string): string => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.fastq.gz') || lower.endsWith('.fq.gz')) return 'FASTQ.GZ';
  if (lower.endsWith('.fastq') || lower.endsWith('.fq')) return 'FASTQ';
  if (lower.endsWith('.bam')) return 'BAM';
  return 'Unknown';
};

// Job status types
type JobStatus =
  | 'queued'
  | 'initialized'
  | 'running'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

const progressMap: Record<JobStatus, number> = {
  queued: 5,
  initialized: 15,
  running: 50,
  processing: 80,
  completed: 100,
  failed: 100,
  cancelled: 100,
};

const statusMessages: Record<JobStatus, string> = {
  queued: 'Queued for processing...',
  initialized: 'Initializing HLA typing pipeline...',
  running: 'Running YARA alignment and OptiType...',
  processing: 'Generating HLA report...',
  completed: 'Analysis complete!',
  failed: 'Analysis failed',
  cancelled: 'Analysis cancelled',
};

const HLATyping = () => {
  // Refs
  const read1InputRef = useRef<HTMLInputElement>(null);
  const read2InputRef = useRef<HTMLInputElement>(null);
  const patientInputRef = useRef<HTMLInputElement>(null);
  const sampleInputRef = useRef<HTMLInputElement>(null);

  // Step 1: ID inputs
  const [localPatientId, setLocalPatientId] = useState('');
  const [localSampleId, setLocalSampleId] = useState('');

  // Step 2: File uploads
  const [, setRead1File] = useState<File | null>(null);
  const [read1FileName, setRead1FileName] = useState<string | null>(null);
  const [read1FileId, setRead1FileId] = useState<number | null>(null);
  const [read1StorageProvider, setRead1StorageProvider] = useState<StorageProvider>('local');
  const [read1CloudURL, setRead1CloudURL] = useState('');

  const [, setRead2File] = useState<File | null>(null);
  const [read2FileName, setRead2FileName] = useState<string | null>(null);
  const [_read2FileId, setRead2FileId] = useState<number | null>(null);
  const [read2StorageProvider, setRead2StorageProvider] = useState<StorageProvider>('local');
  const [read2CloudURL, setRead2CloudURL] = useState('');

  // Shared upload UUID for paired reads
  const sharedUploadUuidRef = useRef<string | null>(null);

  const getOrCreateUploadUuid = (): string => {
    if (!sharedUploadUuidRef.current) {
      sharedUploadUuidRef.current = crypto.randomUUID();
    }
    return sharedUploadUuidRef.current;
  };

  // Job state
  const [jobId, setJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusDetail, setStatusDetail] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Upload state
  const [isUploadingRead1, setIsUploadingRead1] = useState(false);
  const [uploadProgressRead1, setUploadProgressRead1] = useState(0);
  const [isUploadingRead2, setIsUploadingRead2] = useState(false);
  const [uploadProgressRead2, setUploadProgressRead2] = useState(0);
  const [isCloudTransferRead1, setIsCloudTransferRead1] = useState(false);
  const [isCloudTransferRead2, setIsCloudTransferRead2] = useState(false);

  // Previous files state
  interface PreviousFile {
    key: string;
    filename: string;
    relative_path: string;
    pipeline_folder: string;
    file_type: 'fastq' | 'bam';
    size: number;
    last_modified: string | null;
  }
  const [previousFiles, setPreviousFiles] = useState<PreviousFile[]>([]);
  const [isLoadingPreviousFiles, setIsLoadingPreviousFiles] = useState(false);
  const [selectingForRead, setSelectingForRead] = useState<1 | 2 | null>(null);
  const { isOpen: isPreviousFilesOpen, onOpen: onOpenPreviousFiles, onClose: onClosePreviousFiles } = useDisclosure();

  // Feedback modal
  const { showFeedbackModal, setShowFeedbackModal } = useFeedback({
    jobId,
    pipelineType: 'HLA',
    jobStatus: isComplete ? 'completed' : statusDetail,
    delayMs: 5000,
  });

  // PDF viewer state
  const [numPages, setNumPages] = useState<number | null>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  // Computed validation
  const normalizedPatientId = normalizeId(localPatientId);
  const normalizedSampleId = normalizeId(localSampleId);
  const pidOk = !normalizedPatientId || ID_RE.test(normalizedPatientId);
  const sidOk = !!normalizedSampleId && ID_RE.test(normalizedSampleId);
  const idsValid = pidOk && sidOk;

  // S3 upload constants
  const PART_SIZE = 50 * 1024 * 1024;
  const CONCURRENT_UPLOADS = 4;
  const MULTIPART_THRESHOLD = 100 * 1024 * 1024;

  const toast = useToast();

  const showToast = useCallback((
    title: string,
    status: 'info' | 'warning' | 'success' | 'error'
  ) =>
    toast({ title, status, duration: 5000, isClosable: true, position: 'top' }), [toast]);

  // Caret-preserving input handler
  const handleTextChange =
    (ref: React.RefObject<HTMLInputElement>, setValue: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, selectionStart } = e.target;
      setValue(value);
      requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.focus();
          const pos = selectionStart ?? value.length;
          try {
            ref.current.setSelectionRange(pos, pos);
          } catch {}
        }
      });
    };

  // Fetch job results (validates job completion)
  const fetchJobResults = useCallback(async (id: number, retries: number = 3): Promise<boolean> => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(BACKEND_API.HLA_TYPING.RESULTS(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Failed to fetch results:', errorData);
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchJobResults(id, retries - 1);
        }
        showToast(`Failed to fetch results: ${errorData.detail || 'Unknown error'}`, 'error');
        return false;
      }
      
      // Results available - job is complete
      return true;
    } catch (err) {
      console.error('Failed to fetch results:', err);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return fetchJobResults(id, retries - 1);
      }
      showToast('Network error while fetching results. Please try again.', 'error');
      return false;
    }
  }, [showToast]);

  // URL search params for direct navigation
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlJobId = searchParams.get('jobId');
    const urlView = searchParams.get('view');
    if (urlJobId && urlView === 'completed') {
      const parsedJobId = parseInt(urlJobId, 10);
      if (!isNaN(parsedJobId)) {
        setJobId(parsedJobId);
        setIsComplete(true);
        setProgress(100);
        fetchJobResults(parsedJobId);
      }
    }
  }, [searchParams, fetchJobResults]);

  // Job status polling
  useEffect(() => {
    if (!jobId) return;
    if (isComplete) return;
    
    const pollStatus = async () => {
      const token = localStorage.getItem('access_token');
      try {
        const res = await fetch(BACKEND_API.HLA_TYPING.STATUS(jobId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('Status poll failed:', res.status);
          return;
        }
        const data = await res.json();
        const status = data.status as JobStatus;
        
        setStatusDetail(statusMessages[status] || status);
        setProgress(data.progress || progressMap[status] || 0);
        
        if (status === 'completed') {
          setIsComplete(true);
          setIsAnalyzing(false);
          fetchJobResults(jobId);
          return true;
        } else if (status === 'failed' || status === 'cancelled') {
          setIsAnalyzing(false);
          showToast(`Analysis ${status}`, status === 'failed' ? 'error' : 'warning');
          return true;
        }
        return false;
      } catch (err) {
        console.error('Status poll error:', err);
        return false;
      }
    };
    
    pollStatus();
    
    const pollInterval = setInterval(async () => {
      const shouldStop = await pollStatus();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [jobId, isComplete, fetchJobResults, showToast]);

  // S3 upload helpers
  const getS3PresignedUrl = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null, readNumber?: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ filename, content_type: contentType });
    if (existingUuid) params.append('upload_uuid', existingUuid);
    if (readNumber) params.append('read_number', readNumber.toString());
    const res = await fetch(`${BACKEND_API.S3_HLA.PRESIGN}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get presigned URL');
    return await res.json();
  };

  const initiateS3MultipartUpload = async (filename: string, contentType: string = 'application/octet-stream', existingUuid?: string | null, readNumber?: number) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_HLA.MULTIPART_INITIATE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ filename, content_type: contentType, upload_uuid: existingUuid || undefined, read_number: readNumber || undefined }),
    });
    if (!res.ok) throw new Error('Failed to initiate multipart upload');
    return await res.json();
  };

  const getS3PartPresignedUrl = async (key: string, uploadId: string, partNumber: number) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ key, upload_id: uploadId, part_number: partNumber.toString() });
    const res = await fetch(`${BACKEND_API.S3_HLA.MULTIPART_PRESIGN_PART}?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to get presigned URL for part ${partNumber}`);
    return await res.json();
  };

  const completeS3MultipartUpload = async (uploadId: string, key: string, parts: { PartNumber: number; ETag: string }[], fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_HLA.MULTIPART_COMPLETE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ upload_id: uploadId, key, parts, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to complete multipart upload');
    return await res.json();
  };

  const confirmS3Upload = async (key: string, filename: string, fileType: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(BACKEND_API.S3_HLA.CONFIRM_UPLOAD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ key, filename, file_type: fileType }),
    });
    if (!res.ok) throw new Error('Failed to confirm upload');
    return await res.json();
  };

  const uploadToS3Single = async (file: File, presignedUrl: string, contentType: string, setProgressFn: (p: number) => void) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);
      
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgressFn(Math.round((e.loaded / e.total) * 100));
        }
      };
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      };
      
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  };

  const uploadToS3Multipart = async (file: File, uploadId: string, key: string, setProgressFn: (p: number) => void) => {
    const numParts = Math.ceil(file.size / PART_SIZE);
    const parts: { PartNumber: number; ETag: string }[] = new Array(numParts);
    let uploadedBytes = 0;

    const uploadPart = async (partNumber: number): Promise<void> => {
      const start = (partNumber - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const chunk = file.slice(start, end);

      const { upload_url } = await getS3PartPresignedUrl(key, uploadId, partNumber);

      const etag = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) resolve(etag.replace(/"/g, ''));
            else reject(new Error('No ETag in response'));
          } else {
            reject(new Error(`Part upload failed: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(chunk);
      });

      parts[partNumber - 1] = { PartNumber: partNumber, ETag: etag };
      uploadedBytes += (end - start);
      setProgressFn(Math.round((uploadedBytes / file.size) * 100));
      markActivityNow(); // Keep session alive during long uploads
    };

    for (let i = 0; i < numParts; i += CONCURRENT_UPLOADS) {
      const batch = [];
      for (let j = i; j < Math.min(i + CONCURRENT_UPLOADS, numParts); j++) {
        batch.push(uploadPart(j + 1));
      }
      await Promise.all(batch);
    }

    return parts;
  };

  // Local file upload handler
  const handleLocalFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, readNumber: 1 | 2) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!idsValid) {
      showToast('Please enter valid Sample ID before uploading.', 'warning');
      return;
    }

    const setFile = readNumber === 1 ? setRead1File : setRead2File;
    const setFileName = readNumber === 1 ? setRead1FileName : setRead2FileName;
    const setFileId = readNumber === 1 ? setRead1FileId : setRead2FileId;
    const setIsUploading = readNumber === 1 ? setIsUploadingRead1 : setIsUploadingRead2;
    const setUploadProgress = readNumber === 1 ? setUploadProgressRead1 : setUploadProgressRead2;
    const setIsCloudTransfer = readNumber === 1 ? setIsCloudTransferRead1 : setIsCloudTransferRead2;

    setFile(file);
    setIsUploading(true);
    setIsCloudTransfer(false);
    setUploadProgress(0);

    try {
      const contentType = 'application/octet-stream';
      const fileType = getFileTypeFromName(file.name);
      const existingUuid = getOrCreateUploadUuid();
      
      if (file.size > MULTIPART_THRESHOLD) {
        const { upload_id, key } = await initiateS3MultipartUpload(file.name, contentType, existingUuid, readNumber);
        const parts = await uploadToS3Multipart(file, upload_id, key, setUploadProgress);
        const result = await completeS3MultipartUpload(upload_id, key, parts, fileType);
        
        setFileName(file.name);
        setFileId(result.file_id);
        showToast(`Read ${readNumber} uploaded successfully!`, 'success');
      } else {
        const { upload_url, key } = await getS3PresignedUrl(file.name, contentType, existingUuid, readNumber);
        await uploadToS3Single(file, upload_url, contentType, setUploadProgress);
        const result = await confirmS3Upload(key, file.name, fileType);
        
        setFileName(file.name);
        setFileId(result.file_id);
        showToast(`Read ${readNumber} uploaded successfully!`, 'success');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      showToast(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Cloud URL submission
  const handleCloudURLSubmit = async (readNumber: 1 | 2) => {
    const cloudURL = readNumber === 1 ? read1CloudURL : read2CloudURL;
    const setFileName = readNumber === 1 ? setRead1FileName : setRead2FileName;
    const setFileId = readNumber === 1 ? setRead1FileId : setRead2FileId;
    const setIsUploading = readNumber === 1 ? setIsUploadingRead1 : setIsUploadingRead2;
    const setUploadProgress = readNumber === 1 ? setUploadProgressRead1 : setUploadProgressRead2;
    const setIsCloudTransfer = readNumber === 1 ? setIsCloudTransferRead1 : setIsCloudTransferRead2;

    if (!cloudURL.trim() || !idsValid) {
      showToast('Please enter a valid URL and Sample ID', 'warning');
      return;
    }

    const urlFileName = cloudURL.split('/').pop()?.split('?')[0] || 'cloud_file.fastq.gz';
    const fileType = getFileTypeFromName(urlFileName);
    const existingUuid = getOrCreateUploadUuid();

    setIsUploading(true);
    setIsCloudTransfer(false);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('access_token');
      
      const res = await fetch(BACKEND_API.S3_HLA.TRANSFER_FROM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_url: cloudURL,
          filename: urlFileName,
          upload_uuid: existingUuid,
          read_number: readNumber,
          file_type: fileType,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Transfer failed' }));
        throw new Error(errorData.detail || 'Transfer failed');
      }

      const { transfer_id } = await res.json();
      
      let completed = false;
      while (!completed) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const statusRes = await fetch(BACKEND_API.S3_HLA.TRANSFER_STATUS(transfer_id), {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!statusRes.ok) throw new Error('Failed to get transfer status');
        
        const status = await statusRes.json();
        setUploadProgress(status.progress || 0);
        
        if (status.status === 'completed') {
          completed = true;
          setFileName(urlFileName);
          setFileId(status.file_id);
          showToast(`Read ${readNumber} transferred successfully!`, 'success');
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Transfer failed');
        }
      }
      
    } catch (err) {
      console.error('Cloud transfer failed:', err);
      showToast(`Transfer failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsUploading(false);
      setIsCloudTransfer(false);
      setUploadProgress(0);
    }
  };

  // Clear file upload
  const clearFileUpload = (readNumber: 1 | 2) => {
    if (readNumber === 1) {
      setRead1File(null);
      setRead1FileName(null);
      setRead1FileId(null);
      setRead1CloudURL('');
    } else {
      setRead2File(null);
      setRead2FileName(null);
      setRead2FileId(null);
      setRead2CloudURL('');
    }
  };

  // Start analysis
  const handleAnalysis = async () => {
    // Check if we have a file - either uploaded or from previous files
    const hasRead1FromUpload = !!read1FileId;
    const hasRead1FromPrevious = read1CloudURL.startsWith('s3://');
    
    if (!hasRead1FromUpload && !hasRead1FromPrevious) {
      showToast('Please upload or select Read 1 file.', 'warning');
      return;
    }
    if (!sidOk) {
      showToast('Please enter a valid Sample ID.', 'warning');
      return;
    }
    
    // For previous files, we don't need upload_uuid
    const isUsingPreviousFiles = hasRead1FromPrevious && !hasRead1FromUpload;
    
    if (!isUsingPreviousFiles && !sharedUploadUuidRef.current) {
      showToast('Upload not completed. Please re-upload the file.', 'error');
      return;
    }

    setIsAnalyzing(true);
    setProgress(5);
    setStatusDetail('Starting analysis...');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      
      if (isUsingPreviousFiles) {
        // Using previous files - pass S3 paths directly
        formData.append('read1_s3_path', read1CloudURL);
        if (read2CloudURL.startsWith('s3://')) {
          formData.append('read2_s3_path', read2CloudURL);
        }
        // Generate a new upload_uuid for results storage
        const newUuid = crypto.randomUUID();
        formData.append('upload_uuid', newUuid);
        sharedUploadUuidRef.current = newUuid;
      } else {
        // Normal upload flow
        formData.append('upload_uuid', sharedUploadUuidRef.current!);
      }
      
      formData.append('sample_id', normalizedSampleId);
      if (normalizedPatientId) {
        formData.append('patient_id', normalizedPatientId);
      }

      const res = await fetch(BACKEND_API.HLA_TYPING.RUN, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Request failed: ${res.status}`);
      }

      const data = await res.json();
      setJobId(data.job_id);
      showToast('Analysis started successfully!', 'success');
    } catch (err) {
      console.error('Failed to start analysis:', err);
      showToast(`Failed to start analysis: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
      setIsAnalyzing(false);
      setProgress(0);
      setStatusDetail('');
    }
  };

  // Reset functions
  const resetJobState = () => {
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setStatusDetail('');
    setIsAnalyzing(false);
  };

  const resetToStart = () => {
    setRead1File(null);
    setRead1FileName(null);
    setRead1FileId(null);
    setRead1CloudURL('');
    setRead2File(null);
    setRead2FileName(null);
    setRead2FileId(null);
    setRead2CloudURL('');
    setProgress(0);
    setIsComplete(false);
    setJobId(null);
    setStatusDetail('');
    setLocalPatientId('');
    setLocalSampleId('');
    setIsAnalyzing(false);
    sharedUploadUuidRef.current = null;
  };

  // Cancel job
  const handleCancelJob = async () => {
    if (!jobId) return;
    setIsCancelling(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.HLA_TYPING.CANCEL(jobId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Cancel failed: ${res.status}`);
      }

      showToast('Job cancelled. You can re-run the analysis.', 'success');
      resetJobState();
    } catch (err) {
      console.error('Failed to cancel job:', err);
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  // Fetch previous files from S3
  const fetchPreviousFiles = async () => {
    setIsLoadingPreviousFiles(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(BACKEND_API.HLA_TYPING.PREVIOUS_FILES, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch previous files');
      }

      const data = await res.json();
      setPreviousFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching previous files:', err);
      showToast('Failed to load previous files', 'error');
    } finally {
      setIsLoadingPreviousFiles(false);
    }
  };

  // Open previous files modal
  const openSelectPrevious = (readNumber: 1 | 2) => {
    setSelectingForRead(readNumber);
    fetchPreviousFiles();
    onOpenPreviousFiles();
  };

  // Select a previous file
  const selectPreviousFile = (file: PreviousFile) => {
    if (!selectingForRead) return;

    if (selectingForRead === 1) {
      setRead1FileName(file.filename);
      // Store the S3 key as the cloud URL for use in job submission
      setRead1CloudURL(`s3://seq4life-storage/${file.key}`);
      setRead1StorageProvider('previous');
      setRead1FileId(null); // No file upload record, using existing S3 path
    } else {
      setRead2FileName(file.filename);
      setRead2CloudURL(`s3://seq4life-storage/${file.key}`);
      setRead2StorageProvider('previous');
      setRead2FileId(null);
    }

    showToast(`Selected: ${file.filename}`, 'success');
    onClosePreviousFiles();
    setSelectingForRead(null);
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // ==================== UI COMPONENTS ====================

  const IdInputsRow = () => (
    <Flex mt={8} direction="column" maxW="400px" mx="auto" gap={6}>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Sample ID*
        </Text>
        <Input
          placeholder="Enter the sample name or ID"
          value={localSampleId}
          onChange={handleTextChange(sampleInputRef, setLocalSampleId)}
          ref={sampleInputRef}
          maxLength={32}
          borderColor={localSampleId && !sidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
        />
        {localSampleId && !sidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
      <Box>
        <Text mb={1} fontWeight="medium" fontSize="lg" textAlign="left">
          Patient ID
        </Text>
        <Input
          placeholder="Enter the patient ID"
          value={localPatientId}
          onChange={handleTextChange(patientInputRef, setLocalPatientId)}
          ref={patientInputRef}
          maxLength={32}
          borderColor={localPatientId && !pidOk ? 'red.400' : 'surface.300'}
          focusBorderColor="primary.500"
          autoComplete="off"
          spellCheck={false}
          size="lg"
          bg="white"
          borderRadius="md"
          _hover={{ bg: 'surface.100' }}
          _focus={{ bg: 'white', borderColor: 'primary.500', boxShadow: 'none' }}
        />
        {localPatientId && !pidOk && (
          <Text mt={1} fontSize="sm" color="red.500" textAlign="left">
            3–32 chars: A–Z, 0–9, _ or -
          </Text>
        )}
      </Box>
    </Flex>
  );

  const FileUploadSection = ({ readNumber, isOptional = false }: { readNumber: 1 | 2; isOptional?: boolean }) => {
    const fileName = readNumber === 1 ? read1FileName : read2FileName;
    const fileId = readNumber === 1 ? read1FileId : _read2FileId;
    const storageProvider = readNumber === 1 ? read1StorageProvider : read2StorageProvider;
    const setStorageProvider = readNumber === 1 ? setRead1StorageProvider : setRead2StorageProvider;
    const cloudURL = readNumber === 1 ? read1CloudURL : read2CloudURL;
    const setCloudURL = readNumber === 1 ? setRead1CloudURL : setRead2CloudURL;
    const inputRef = readNumber === 1 ? read1InputRef : read2InputRef;
    const isUploading = readNumber === 1 ? isUploadingRead1 : isUploadingRead2;
    const uploadProgress = readNumber === 1 ? uploadProgressRead1 : uploadProgressRead2;
    const isCloudTransfer = readNumber === 1 ? isCloudTransferRead1 : isCloudTransferRead2;
    
    // Determine if this is a reused file from previous uploads
    const isFromPrevious = cloudURL.startsWith('s3://') && !fileId;

    return (
      <Box mt={0} p={6} border="2px solid rgba(0,0,0,0.06)" borderRadius="2xl" bg="white" w="100%">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="semibold" color="primary.500">
            Read {readNumber} {isOptional && '(optional)'}
          </Text>
          {fileName && (
            <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<CloseIcon boxSize={3} />} onClick={() => clearFileUpload(readNumber)}>
              Remove
            </Button>
          )}
        </Flex>

        {fileName ? (
          <Flex align="center" justify="space-between" p={4} bg="surface.100" borderRadius="xl">
            <Text fontWeight="medium" color="primary.500" isTruncated maxW="400px">{fileName}</Text>
            <Badge colorScheme={isFromPrevious ? 'blue' : 'green'} fontSize="sm" px={2} py={1}>
              {isFromPrevious ? 'From Previous' : 'Uploaded'}
            </Badge>
          </Flex>
        ) : (
          <>
            <StorageProviderSelector 
              selectedProvider={storageProvider} 
              onChange={(provider) => {
                // Check if Sample ID is valid before allowing file selection
                if (!idsValid && provider !== 'local') {
                  showToast('Please enter a valid Sample ID first (3-32 characters: A-Z, 0-9, _ or -)', 'warning');
                  return;
                }
                setStorageProvider(provider);
                // If previous files selected, open modal directly
                if (provider === 'previous') {
                  openSelectPrevious(readNumber);
                }
              }} 
              showPrevious={true} 
            />

            {storageProvider === 'local' && (
              <Flex mt={4} justify="center" align="center" gap={4} wrap="wrap">
                <Input
                  type="file"
                  ref={inputRef}
                  display="none"
                  accept=".fastq,.fq,.fastq.gz,.fq.gz,.bam,.gz,application/gzip,application/x-gzip"
                  onChange={(e) => handleLocalFileUpload(e, readNumber)}
                  disabled={!idsValid}
                />
                <Button
                  leftIcon={<Icon as={FiHardDrive} />}
                  bg="primary.500"
                  color="white"
                  fontWeight="bold"
                  px={8}
                  py={6}
                  borderRadius="full"
                  onClick={() => inputRef.current?.click()}
                  isDisabled={!idsValid || isUploading}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
                  _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
                  transition="all 0.18s ease-in-out"
                >
                  Upload File
                </Button>
              </Flex>
            )}

            {storageProvider !== 'local' && storageProvider !== 'previous' && (
              <>
                <CloudStorageURLInput
                  provider={storageProvider}
                  value={cloudURL}
                  onChange={setCloudURL}
                  onSubmit={() => handleCloudURLSubmit(readNumber)}
                  isDisabled={!idsValid}
                />
                <StorageProviderInstructions provider={storageProvider} />
              </>
            )}
          </>
        )}

        {isUploading && (
          <Box mt={4}>
            <Progress
              value={isCloudTransfer ? undefined : uploadProgress}
              isIndeterminate={isCloudTransfer}
              colorScheme="purple"
              size="lg"
              hasStripe
              isAnimated
              borderRadius="full"
            />
            <Text mt={2} fontSize="sm" color="muted.400" textAlign="center">
              {isCloudTransfer ? 'Transferring from cloud storage...' : `${uploadProgress}% uploaded`}
            </Text>
          </Box>
        )}
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================

  const showUploadForm = !isAnalyzing && !isComplete;
  const showProgress = isAnalyzing && !isComplete;
  const showResults = isComplete;

  return (
    <Box p={8} textAlign="center" position="relative">
      {/* Page Title */}
      <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="primary.500" mb={4}>
        HLA Typing Analysis
      </Text>

      {/* Description Box */}
      <Text
        fontSize={{ base: 'md', md: 'lg' }}
        color="primary.500"
        maxW={{ base: '900px', md: '1000px' }}
        mx="auto"
        textAlign={{ base: 'left', md: 'justify' }}
        mb={8}
        lineHeight={1.7}
      >
        Determine HLA Class I genotypes (HLA-A, HLA-B, HLA-C) from DNA sequencing data 
        using OptiType, a precision HLA typing algorithm. Our pipeline extracts HLA-relevant 
        reads using YARA alignment and applies integer linear programming to identify the 
        most likely allele combinations. Results are provided at 4-digit resolution for 
        applications in transplant compatibility, pharmacogenomics, and immunotherapy research.
      </Text>

      {/* Upload Form */}
      {showUploadForm && (
        <>
          <IdInputsRow />

          <Text fontSize="xl" color="muted.400" mt={10} mb={2}>
            Upload FASTQ or BAM files from your local storage or cloud
          </Text>

          <Flex direction={{ base: 'column', lg: 'row' }} gap={6} w="100%" maxW="1440px" mx="auto" justify="center">
            <Box flex="1" maxW={{ base: '100%', lg: '700px' }}>
              <FileUploadSection readNumber={1} />
            </Box>
            <Box flex="1" maxW={{ base: '100%', lg: '700px' }}>
              <FileUploadSection readNumber={2} isOptional />
            </Box>
          </Flex>

          <Box mt={6} p={4} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg" maxW="700px" mx="auto">
            <Flex align="center" gap={3}>
              <InfoIcon color="blue.500" boxSize={5} />
              <Text fontSize="sm" color="blue.700" fontWeight="medium" textAlign="left">
                This pipeline supports both <strong>paired-end FASTQ</strong> and <strong>BAM</strong> files. 
                For BAM input, only Read 1 is required. HLA typing is performed on HLA Class I genes 
                (HLA-A, HLA-B, HLA-C).
              </Text>
            </Flex>
          </Box>

          <Button
            mt={10}
            mb="140px"
            bg="primary.500"
            fontSize="2xl"
            color="white"
            fontWeight="bold"
            px={12}
            py={7}
            borderRadius="full"
            onClick={handleAnalysis}
            isDisabled={!idsValid || !read1FileName}
            _hover={{ bg: 'secondary.500', transform: 'scale(1.03)' }}
            _active={{ bg: 'primary.500', transform: 'scale(0.99)' }}
            transition="all 0.18s ease-in-out"
          >
            Run HLA Typing
          </Button>
        </>
      )}

      {/* Progress View */}
      {showProgress && (
        <Box textAlign="center" p={8}>
          <Text as="h3" fontSize="3xl" mt={10} mx="auto" maxW={{ base: '280px', md: '640px' }} whiteSpace="normal" overflowWrap="break-word" lineHeight="1.3" color="primary.500">
            Typing your HLA genes – sit tight while we prepare your results.
          </Text>

          <Text mt={4} color="muted.500" mx="auto" maxW={{ base: '280px', md: '500px' }} fontSize="md" fontStyle="italic">
            This might take a while. We'll email you when your results are ready – feel free to come back later.
          </Text>

          {!!statusDetail && (
            <Text mt={4} color="muted.400" mx="auto" maxW={{ base: '260px', md: '600px' }} whiteSpace="normal" overflowWrap="break-word" fontSize="lg">
              {statusDetail}
            </Text>
          )}

          {progress > 0 && (
            <Text mt={4} mb={2} fontSize="xl" color="primary.500" fontWeight="bold">
              {progress}%
            </Text>
          )}

          <Flex mt={6} justify="center" align="center" maxW="500px" mx="auto">
            <Box position="relative" w="100%" h="24px" bg="surface.300" borderRadius="full" overflow="hidden">
              <Box position="absolute" left={0} top={0} bottom={0} width={`${progress}%`} bg="primary.500" borderRadius="full" transition="width 0.5s ease" />
            </Box>
          </Flex>

          {jobId && (
            <Button mt={8} colorScheme="red" onClick={handleCancelJob} isLoading={isCancelling} loadingText="Cancelling...">
              Cancel
            </Button>
          )}
        </Box>
      )}

      {/* Results View */}
      {showResults && (
        <>
          <Text fontSize="3xl" fontWeight="bold" mt={10} color="primary.500">
            HLA Typing Complete
          </Text>
          <Text fontSize="lg" color="muted.400" mt={2} maxW="600px" mx="auto">
            Your HLA Class I genotype results are ready. Download your report or view it below.
          </Text>

          {/* Download Buttons */}
          <Box mt={10} maxW="600px" mx="auto">
            <Flex mt={4} justify="center" align="center" wrap="wrap" gap={3}>
              <Button
                bg="primary.500"
                color="white"
                fontWeight="bold"
                width="180px"
                py={4}
                leftIcon={<DownloadIcon />}
                _hover={{ bg: 'secondary.500' }}
                onClick={async () => {
                  if (!jobId) {
                    showToast('Job ID is not available.', 'warning');
                    return;
                  }
                  try {
                    const token = localStorage.getItem('access_token');
                    const response = await fetch(`${BACKEND_API.HLA_TYPING.PDF(jobId)}?t=${token}`);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = 'HLA_Report.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                  } catch (err) {
                    console.error('PDF download failed:', err);
                    showToast('Failed to download report', 'error');
                  }
                }}
              >
                Download Report
              </Button>
              <Button
                bg="primary.500"
                color="white"
                fontWeight="bold"
                width="180px"
                py={4}
                _hover={{ bg: 'secondary.500' }}
                onClick={() => {
                  if (!jobId) {
                    showToast('Job ID is not available.', 'warning');
                    return;
                  }
                  const token = localStorage.getItem('access_token');
                  window.open(`${BACKEND_API.HLA_TYPING.PDF(jobId)}?t=${token}`, '_blank', 'noopener,noreferrer');
                }}
              >
                View Report
              </Button>
              <Button
                bg="primary.500"
                color="white"
                fontWeight="bold"
                width="180px"
                py={4}
                leftIcon={<DownloadIcon />}
                _hover={{ bg: 'secondary.500' }}
                onClick={async () => {
                  if (!jobId) {
                    showToast('Job ID is not available.', 'warning');
                    return;
                  }
                  try {
                    showToast('Preparing QC report download...', 'info');
                    const token = localStorage.getItem('access_token');
                    const response = await fetch(`${BACKEND_API.HLA_TYPING.MULTIQC_ZIP(jobId)}?t=${token}`);
                    if (!response.ok) throw new Error('Download failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = 'MultiQC_Report.zip';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(blobUrl);
                    showToast('QC report downloaded', 'success');
                  } catch (err) {
                    console.error('QC download failed:', err);
                    showToast('Failed to download QC report', 'error');
                  }
                }}
              >
                Download QC
              </Button>
            </Flex>

            <Button
              mt={8}
              variant="outline"
              borderColor="primary.500"
              color="primary.500"
              fontWeight="bold"
              px={8}
              py={6}
              borderRadius="full"
              onClick={resetToStart}
              _hover={{ bg: 'surface.100' }}
            >
              Start New Analysis
            </Button>
          </Box>

          {/* PDF Viewer */}
          <Box
            mt={6}
            width="800px"
            maxW="95%"
            mx="auto"
            sx={{ aspectRatio: '210/297' }}
            border="1px solid #ccc"
            borderRadius="md"
            mb="140px"
          >
            {jobId ? (
              <Box
                width="100%"
                height="100%"
                bg="surface.200"
                overflowY="auto"
                display="flex"
                flexDirection="column"
                alignItems="center"
                py={8}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                  },
                }}
              >
                <Document
                  file={`${BACKEND_API.HLA_TYPING.PDF(jobId)}?t=${localStorage.getItem('access_token')}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  externalLinkTarget="_blank"
                  loading={
                    <Center h="100%">
                      <Spinner size="xl" color="primary.500" thickness="4px" />
                    </Center>
                  }
                  error={
                    <Center h="100%">
                      <Text color="red.500">Failed to load PDF.</Text>
                    </Center>
                  }
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Box
                      key={`page_${index + 1}`}
                      mb={6}
                      boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                    >
                      <Page
                        pageNumber={index + 1}
                        width={800}
                        renderTextLayer={false}
                        renderAnnotationLayer={true}
                      />
                    </Box>
                  ))}
                </Document>
              </Box>
            ) : (
              <Box mt={4} textAlign="center" p={8}>
                <Text color="red.500" fontSize="lg" fontWeight="bold" mb={2}>
                  Report preview not available.
                </Text>
                <Text color="muted.400" mb={4}>
                  The PDF report may still be processing. Please wait a moment and click "Refresh Report" above.
                </Text>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Chatbot */}
      <Box flexShrink={0}>
        <Chatbot />
      </Box>

      {/* Previous Files Modal */}
      <Modal isOpen={isPreviousFilesOpen} onClose={onClosePreviousFiles} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="700px">
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Select from Previous Uploads</Text>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<RepeatIcon />}
                onClick={fetchPreviousFiles}
                isLoading={isLoadingPreviousFiles}
              >
                Refresh
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {isLoadingPreviousFiles ? (
              <Center py={10}>
                <Spinner size="xl" color="primary.500" />
              </Center>
            ) : previousFiles.length === 0 ? (
              <Center py={10}>
                <VStack>
                  <Icon as={FiFolder} boxSize={12} color="muted.300" />
                  <Text color="muted.400" fontSize="lg">No previous files found</Text>
                  <Text color="muted.300" fontSize="sm">Upload files from other pipelines to reuse them here</Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {/* Group by pipeline folder */}
                {Object.entries(
                  previousFiles.reduce((acc, file) => {
                    const folder = file.pipeline_folder;
                    if (!acc[folder]) acc[folder] = [];
                    acc[folder].push(file);
                    return acc;
                  }, {} as Record<string, PreviousFile[]>)
                ).map(([folder, files]) => (
                  <Box key={folder}>
                    <Text fontSize="sm" fontWeight="bold" color="muted.500" textTransform="uppercase" mb={2}>
                      {folder.replace(/_/g, ' ')}
                    </Text>
                    <VStack spacing={1} align="stretch">
                      {files.map((file) => (
                        <Flex
                          key={file.key}
                          p={3}
                          bg="surface.50"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="surface.200"
                          cursor="pointer"
                          _hover={{ bg: 'surface.100', borderColor: 'primary.300' }}
                          onClick={() => selectPreviousFile(file)}
                          align="center"
                          justify="space-between"
                        >
                          <HStack spacing={3} flex={1} minW={0}>
                            <Icon as={FiFolder} color="primary.400" />
                            <Text fontWeight="medium" color="text.primary" isTruncated maxW="100%">
                              {file.filename}
                            </Text>
                          </HStack>
                          <HStack spacing={2}>
                            <Badge colorScheme={file.file_type === 'fastq' ? 'green' : 'blue'} fontSize="xs">
                              {file.file_type.toUpperCase()}
                            </Badge>
                            <Text fontSize="xs" color="muted.400" whiteSpace="nowrap">
                              {formatFileSize(file.size)}
                            </Text>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    <Divider my={3} />
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Feedback Modal */}
      {jobId && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          jobId={jobId}
          pipelineType="HLA"
          onSubmitSuccess={() => setShowFeedbackModal(false)}
        />
      )}
    </Box>
  );
};

export default HLATyping;
